import { useState, useEffect, useCallback } from 'react';
import { announcementService } from '../services';

export const useAnnouncements = (filters = {}) => {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  });

  const fetchAnnouncements = useCallback(async (newFilters = {}) => {
    try {
      setLoading(true);
      setError(null);
      const params = { ...filters, ...newFilters, page: pagination.page, limit: pagination.limit };
      const response = await announcementService.getAnnouncements(params);
      
      if (response.data.success) {
        setAnnouncements(response.data.data.announcements || []);
        setPagination(prev => ({
          ...prev,
          total: response.data.data.pagination?.totalItems || 0,
          totalPages: response.data.data.pagination?.totalPages || 0,
        }));
      } else {
        setAnnouncements([]);
        setPagination(prev => ({
          ...prev,
          total: 0,
          totalPages: 0,
        }));
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch announcements');
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.page, pagination.limit]);

  useEffect(() => {
    fetchAnnouncements();
  }, [fetchAnnouncements]);

  const createAnnouncement = useCallback(async (data) => {
    try {
      const response = await announcementService.createAnnouncement(data);
      if (response.data.success) {
        setAnnouncements(prev => [response.data.data, ...prev]);
        setPagination(prev => ({
          ...prev,
          total: prev.total + 1,
        }));
        return { success: true, data: response.data.data };
      }
      return { success: false, message: response.data.message };
    } catch (err) {
      const message = err.response?.data?.message || 'Failed to create announcement';
      setError(message);
      return { success: false, message };
    }
  }, []);

  const updateAnnouncement = useCallback(async (id, data) => {
    try {
      const response = await announcementService.updateAnnouncement(id, data);
      if (response.data.success) {
        setAnnouncements(prev => 
          prev.map(announcement => 
            announcement.id === id ? response.data.data : announcement
          )
        );
        return { success: true, data: response.data.data };
      }
      return { success: false, message: response.data.message };
    } catch (err) {
      const message = err.response?.data?.message || 'Failed to update announcement';
      setError(message);
      return { success: false, message };
    }
  }, []);

  const deleteAnnouncement = useCallback(async (id) => {
    try {
      const response = await announcementService.deleteAnnouncement(id);
      if (response.data.success) {
        setAnnouncements(prev => prev.filter(announcement => announcement.id !== id));
        setPagination(prev => ({
          ...prev,
          total: prev.total - 1,
        }));
        return { success: true };
      }
      return { success: false, message: response.data.message };
    } catch (err) {
      const message = err.response?.data?.message || 'Failed to delete announcement';
      setError(message);
      return { success: false, message };
    }
  }, []);

  const changePage = useCallback((newPage) => {
    setPagination(prev => ({
      ...prev,
      page: newPage,
    }));
  }, []);

  const changeLimit = useCallback((newLimit) => {
    setPagination(prev => ({
      ...prev,
      limit: newLimit,
      page: 1,
    }));
  }, []);

  const refreshAnnouncements = useCallback(() => {
    fetchAnnouncements();
  }, [fetchAnnouncements]);

  return {
    announcements,
    loading,
    error,
    pagination,
    createAnnouncement,
    updateAnnouncement,
    deleteAnnouncement,
    changePage,
    changeLimit,
    refreshAnnouncements,
    setError
  };
};

// Hook for announcement count (for bell notification)
export const useAnnouncementCount = () => {
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchCount = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await announcementService.getAnnouncementCount();
      
      if (response.data.success) {
        setCount(response.data.data.count || 0);
      } else {
        setCount(0);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch announcement count');
      setCount(0);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCount();
  }, [fetchCount]);

  const refreshCount = useCallback(() => {
    fetchCount();
  }, [fetchCount]);

  return {
    count,
    loading,
    error,
    refreshCount
  };
};